// Invitation acceptance: scan QR → enter code → save recovery codes → signed in.

import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { api, ApiError } from "../lib/api";

interface Props { onDone: () => void }

interface InvitePayload {
  email: string | null;
  name: string | null;
  secret: string;
  otpauthUri: string;
}

export default function Invite({ onDone }: Props) {
  const [data, setData] = useState<InvitePayload | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string>("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);

  const token = new URLSearchParams(window.location.search).get("t") || "";

  useEffect(() => {
    if (!token) { setError("Missing invite token"); return; }
    api<InvitePayload>(`/api/auth/invite?t=${encodeURIComponent(token)}`)
      .then(async (r) => {
        setData(r);
        setQrDataUrl(await QRCode.toDataURL(r.otpauthUri, { margin: 1, width: 240 }));
      })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : "Invite error"));
  }, [token]);

  async function confirm(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const r = await api<{ recoveryCodes: string[] }>("/api/auth/invite", {
        method: "POST",
        body: JSON.stringify({ token, code }),
      });
      setRecoveryCodes(r.recoveryCodes);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not confirm");
    } finally { setBusy(false); }
  }

  if (recoveryCodes) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="card w-full max-w-md space-y-4 p-6">
          <div className="text-lg font-semibold tracking-tightish">Save these recovery codes</div>
          <div className="text-sm text-slate-600">
            Store these somewhere safe. Each can be used once if you lose access to your authenticator app.
          </div>
          <div className="rounded-md bg-slate-50 p-3 font-mono text-sm ring-1 ring-slate-100">
            {recoveryCodes.map((c) => <div key={c}>{c}</div>)}
          </div>
          <button className="btn-primary w-full" onClick={onDone}>I've saved them — continue</button>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="card max-w-sm space-y-3 p-6 text-center">
          <div className="text-lg font-semibold text-rose-700">Invite invalid</div>
          <div className="text-sm text-slate-600">{error}</div>
          <p className="text-xs text-slate-500">Ask your admin to resend the invite.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <form onSubmit={confirm} className="card w-full max-w-md space-y-4 p-6">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-gradient-to-br from-slate-800 to-slate-950 text-[12px] font-bold text-white shadow-soft">M</div>
          <div className="text-base font-semibold tracking-tightish">MailBIT Tools</div>
        </div>
        <div className="text-lg font-semibold tracking-tightish">
          Welcome{data?.name ? `, ${data.name.split(" ")[0]}` : ""}
        </div>
        <div className="text-sm text-slate-600">
          Set up two-factor authentication to access your account at <code>{data?.email}</code>.
        </div>

        <ol className="space-y-3 text-sm text-slate-700">
          <li>
            <div className="font-medium">1. Scan with Google Authenticator / Authy / 1Password</div>
            {qrDataUrl
              ? <img src={qrDataUrl} alt="TOTP QR code" className="mx-auto mt-2 h-60 w-60" />
              : <div className="mt-2 text-slate-500">Generating QR…</div>}
            {data?.secret && (
              <details className="mt-2 text-xs text-slate-500">
                <summary className="cursor-pointer">Can't scan? Enter manually</summary>
                <div className="mt-1 break-all font-mono">{data.secret}</div>
              </details>
            )}
          </li>
          <li>
            <div className="font-medium">2. Enter the 6-digit code to confirm</div>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode="numeric" pattern="\d{6}" maxLength={6} required
              className="mt-2 w-full text-center font-mono text-lg tracking-widest"
              placeholder="123456"
            />
          </li>
        </ol>
        {error && <div className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700 ring-1 ring-rose-100">{error}</div>}
        <button type="submit" disabled={busy || !data?.secret} className="btn-primary w-full">
          {busy ? "Confirming…" : "Confirm and finish setup"}
        </button>
      </form>
    </div>
  );
}
